import React, { useState } from "react";
import { notify } from "../Notification";
import usePlayer, { player } from "../../hooks/usePlayer";
import { ExpandIcon, MoreIcon, PanelIcon, SavedIcon, VerifiedIcon, VideoIcon } from "./icons";

const FollowButton = ({ initial }) => {
  const [following, setFollowing] = useState(initial);
  return (
    <button type="button" className="sp-follow" onClick={() => setFollowing((f) => !f)} aria-pressed={following}>
      {following ? "Following" : "Follow"}
    </button>
  );
};

const CardHead = ({ title, action }) => (
  <div className="sp-npv-box-head">
    <h3>{title}</h3>
    {action && <span aria-hidden="true">{action}</span>}
  </div>
);

// Spotify's Now Playing view: what it plays from, then the song's Canvas (or its cover), title and
// artists, and below them what Sylvester's screenshots show for Make Them Cry: the lyrics preview,
// related videos, the artist, credits, and what plays next. Its header stays at the top; the panel
// button closes it (the cover in the player bar opens it again).
const NowPlayingView = ({ onClose }) => {
  const { playlist, track, next } = usePlayer();
  const [scrolled, setScrolled] = useState(false);
  const saved = track.saved || playlist.id === "liked";
  const about = playlist.about && track.artists.includes(playlist.about.name) ? playlist.about : null;

  const heading = (
    <div className="sp-npv-heading">
      <span className="sp-npv-title">{track.title}</span>
      <span className="sp-npv-artist">{track.artist}</span>
      {saved && <SavedIcon className="sp-npv-saved" aria-label="Saved" />}
    </div>
  );

  return (
    <aside
      className="sp-panel sp-npv"
      aria-label="Now playing"
      style={{ "--sp-color": playlist.color }}
      onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 0)}
    >
      <header className={`sp-npv-header ${scrolled ? "is-scrolled" : ""}`}>
        <button type="button" className="sp-npv-tool sp-npv-close" onClick={onClose} aria-label="Hide Now Playing view">
          <PanelIcon />
        </button>
        <h2 className="sp-npv-context">{playlist.name}</h2>
        <button
          type="button"
          className="sp-npv-tool"
          onClick={() => notify("That menu works in Spotify itself, not in this replica.")}
          aria-label="More options"
        >
          <MoreIcon />
        </button>
        <span className="sp-npv-tool" aria-hidden="true">
          <ExpandIcon />
        </span>
      </header>

      {track.canvas ? (
        <div className="sp-npv-canvas">
          <img src={track.canvas} alt="" draggable={false} />
          {heading}
        </div>
      ) : (
        <div className="sp-npv-plain">
          <img src={track.image} alt="" className="sp-npv-cover" draggable={false} />
          {heading}
        </div>
      )}

      {track.lyrics && (
        <section className="sp-npv-card sp-npv-lyrics">
          <h3>Lyrics preview</h3>
          {track.lyrics.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </section>
      )}

      {track.videos && (
        <section className="sp-npv-videos">
          <h3>Related music videos</h3>
          <div className="sp-npv-video-grid">
            {track.videos.map((video) => (
              <div key={video.title} className="sp-npv-video">
                <img src={video.image} alt="" draggable={false} />
                <span className="sp-npv-video-title">{video.title}</span>
                <span className="sp-npv-video-sub">{video.artists}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {about && (
        <section className="sp-npv-box sp-npv-about">
          <div className="sp-npv-about-image">
            <h3>About the artist</h3>
          </div>
          <div className="sp-npv-about-body">
            <p className="sp-npv-about-name">
              {about.name}
              <VerifiedIcon className="sp-verified" aria-label="Verified artist" />
            </p>
            <div className="sp-npv-about-row">
              <span>{about.listeners} monthly listeners</span>
              <FollowButton key={about.name} initial />
            </div>
            <p className="sp-npv-about-bio">{about.bio}…</p>
          </div>
        </section>
      )}

      {track.credits && (
        <section className="sp-npv-box">
          <CardHead title="Credits" action="Show all" />
          {track.credits.map((credit) => (
            <div key={credit.name} className="sp-npv-credit">
              <span className="sp-npv-credit-text">
                <span className="sp-npv-credit-name">{credit.name}</span>
                <span className="sp-npv-credit-role">{credit.role}</span>
              </span>
              {credit.follows && <FollowButton key={credit.name} initial />}
            </div>
          ))}
        </section>
      )}

      {next && (
        <section className="sp-npv-box">
          <CardHead title="Next in queue" action="Open queue" />
          <button type="button" className="sp-npv-next" onClick={player.next} aria-label={`Play ${next.title} next`}>
            <img src={next.image} alt="" draggable={false} />
            <span className="sp-npv-next-text">
              <span className="sp-npv-next-title">{next.title}</span>
              <span className="sp-npv-next-sub">
                {next.explicit && (
                  <span className="sp-explicit" aria-label="Explicit">
                    E
                  </span>
                )}
                {next.video && <VideoIcon className="sp-video-icon" aria-hidden="true" />}
                {next.artists.join(", ")}
              </span>
            </span>
          </button>
        </section>
      )}
    </aside>
  );
};

export default NowPlayingView;
